import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Link,
    SxProps,
    Theme
} from '@mui/material';
import { EventItem, TicketSource } from '../../models/event.ts';


/**
 * Component props for EventHistoryTable
 */
interface EventHistoryTableProps {
    selectedEvent: EventItem | null;
}

/**
 * CSS-in-JS theme variables for consistent styling across the history table
 */
const tableTheme = {
    colors: {
        headerBackground: 'rgba(40, 40, 40, 0.9)',
        oddRowBackground: 'rgba(50, 50, 50, 0.3)',
        evenRowBackground: 'rgba(40, 40, 40, 0.3)',
        borderColor: 'rgba(60, 60, 60, 0.5)',
        borderColorLight: 'rgba(60, 60, 60, 0.3)',
        borderColorVeryLight: 'rgba(60, 60, 60, 0.2)',
        textPrimary: 'text.primary',
        textSecondary: 'text.secondary',
        white: 'white',
        successMain: 'success.main',
        errorMain: 'error.main'
    },
    borders: {
        headerBottom: '2px solid rgba(60, 60, 60, 0.8)',
        cellBorder: '1px solid rgba(60, 60, 60, 0.2)',
        rightBorder: '1px solid rgba(60, 60, 60, 0.3)'
    },
    fonts: {
        small: 'small' as const
    }
};

/**
 * Platform configuration type
 */
interface PlatformConfig {
    name: string;
    color: string;
}

/**
 * Platform with data type
 */
interface PlatformData extends PlatformConfig {
    data?: TicketSource | null;
}

/**
 * Platform configuration with default styling
 */
const PLATFORM_CONFIG: PlatformConfig[] = [
    {
        name: 'Primary',
        color: 'rgba(63, 81, 181, 0.1)'
    },
    {
        name: 'TN',
        color: 'rgba(76, 175, 80, 0.1)'
    },
    {
        name: 'VS',
        color: 'rgba(255, 152, 0, 0.1)'
    },
    {
        name: 'GC',
        color: 'rgba(156, 39, 176, 0.1)'
    }
];

/**
 * Formats a percentage value for display
 * @param value - The percentage value (already in decimal form)
 * @returns Formatted percentage string with 2 decimal places
 */
const formatPercentage = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) {
        return '0.00%';
    }
    return `${value.toFixed(2)}%`;
};

/**
 * Formats a count value for display
 * @param count - The count value
 * @returns Formatted count or 0 if invalid
 */
const formatCount = (count: number | null | undefined): string => {
    if (count === null || count === undefined || isNaN(count)) {
        return '0';
    }
    return count.toLocaleString();
};

/**
 * Props for HeaderCell component
 */
interface HeaderCellProps {
    children: React.ReactNode;
    sx?: SxProps<Theme>;
    align?: 'left' | 'center' | 'right' | 'justify' | 'inherit';
}

/**
 * Reusable table header cell component
 * @param props - Component props
 */
const HeaderCell: React.FC<HeaderCellProps> = ({ children, sx = {}, align, ...props }) => (
    <TableCell
        sx={{
            fontWeight: 'bold',
            color: tableTheme.colors.white,
            borderBottom: tableTheme.borders.headerBottom,
            ...sx
        }}
        align={align}
        {...props}
    >
        {children}
    </TableCell>
);

/**
 * Props for DataCell component
 */
interface DataCellProps {
    children: React.ReactNode;
    sx?: SxProps<Theme>;
}

/**
 * Reusable data cell component for platform values
 * @param props - Component props
 */
const DataCell: React.FC<DataCellProps> = ({ children, sx = {}, ...props }) => (
    <TableCell
        align="center"
        sx={{
            color: tableTheme.colors.textPrimary,
            border: tableTheme.borders.cellBorder,
            ...sx
        }}
        {...props}
    >
        {children}
    </TableCell>
);

/**
 * Metric configuration type
 */
interface MetricConfig {
    key: string;
    label: string;
    type: 'count' | 'percentage' | 'tnSales';
}

/**
 * Metric configuration for table headers
 */
const METRIC_CONFIG: MetricConfig[] = [
    { key: 'current', label: 'Current Available Count', type: 'count' },
    { key: 'twoweeks', label: 'Two Weeks Ago Count', type: 'count' },
    { key: 'month', label: 'Month Ago Count', type: 'count' },
    { key: 'oldest', label: 'Oldest Available Count', type: 'count' },
    { key: 'tnSales2W', label: 'TN Sales 2W', type: 'tnSales' },
    { key: 'tnSalesTotal', label: 'TN Sales Total', type: 'tnSales' }
];

/**
 * Props for PlatformRow component
 */
interface PlatformRowProps {
    platform: PlatformData;
}

/**
 * Platform row component - displays platform name and all metric values horizontally
 * @param props - Component props
 */
const PlatformRow: React.FC<PlatformRowProps> = ({ platform }) => {
    const getMetricValue = (metricKey: string): React.ReactNode => {
        const data = platform.data;
        if (!data) return '0';

        switch (metricKey) {
            case 'current':
                if (platform.name === 'Primary') {
                    return (
                        <span>
                            {formatCount(data.currentAvailableCount)}
                            <br/>
                            <span style={{ color: 'gray', fontSize: '9px' }}>
                            ({formatPercentage(data.currentAvailableCount! / data.oldestAvailableCount!)})
                            </span>
                        </span>
                    );
                }
                return formatCount(data.currentAvailableCount);

            case 'twoweeks':
                if (platform.name === 'Primary') {
                    return (
                        <span>
                            {formatCount(data.twoWeeksAgoCount)}
                            <br/>
                            <span style={{ color: 'gray', fontSize: '9px' }}>
                            ({formatCount(data.currentAvailableCount! - data.twoWeeksAgoCount!)})
                            </span>
                        </span>
                    );
                }
                return formatCount(data.twoWeeksAgoCount);

            case 'month':
                if (platform.name === 'Primary') {
                    return (
                        <span>
                            {formatCount(data.monthAgoCount)}
                            <br/>
                            <span style={{ color: 'gray', fontSize: '9px' }}>
                            ({formatCount(data.currentAvailableCount! - data.monthAgoCount!)})
                            </span>
                        </span>
                    );
                }
                return formatCount(data.monthAgoCount);

            case 'oldest':
                if (platform.name === 'Primary') {
                    return (
                        <span>
                            {formatCount(data.oldestAvailableCount)}
                            <br/>
                            <span style={{ color: 'gray', fontSize: '9px' }}>
                            ({formatCount(data.currentAvailableCount! - data.oldestAvailableCount!)})
                            </span>
                        </span>
                    );
                }
                return formatCount(data.oldestAvailableCount);

            case 'percent':
                return formatPercentage(data.percentChangeOldest);

            case 'tnSales2W':
                if (platform.name === 'TN') {
                    return formatCount(data.twoWeeksTicketnetworkConfirmedSalesCount || 0);
                }
                return '';

            case 'tnSalesTotal':
                if (platform.name === 'TN') {
                    return formatCount(data.totalTicketnetworkConfirmedSalesCount || 0);
                }
                return '';

            default:
                return '0';
        }
    };

    const getPercentageCellColor = (value: number | undefined): string => {
        if (!value) return tableTheme.colors.textPrimary;
        if (value > 0) return tableTheme.colors.successMain;
        if (value < 0) return tableTheme.colors.errorMain;
        return tableTheme.colors.textPrimary;
    };

    return (
        <TableRow sx={{
            '&:nth-of-type(odd)': { backgroundColor: tableTheme.colors.oddRowBackground },
            '&:nth-of-type(even)': { backgroundColor: tableTheme.colors.evenRowBackground }
        }}>
            {/* Platform Name Cell */}
            <TableCell sx={{
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: platform.color,
                borderRight: tableTheme.borders.rightBorder,
                border: tableTheme.borders.cellBorder,
                minWidth: 60,
                textAlign: 'center'
            }}>
                <Link
                    href={platform.data?.url}
                    target="_blank"
                    underline='hover'
                    rel="noopener noreferrer"
                    sx={{
                        color: 'white',
                        cursor: 'pointer',
                        '&:hover': {
                            color: 'lightgreen',
                            textDecoration: 'underline'
                        }
                    }}
                >
                    {platform.name}
                </Link>
            </TableCell>

            {/* Metric Value Cells */}
            {METRIC_CONFIG.map((metric) => (
                <DataCell
                    key={`${platform.name}-${metric.key}`}
                    sx={{
                        ...(metric.type === 'percentage' && {
                            color: getPercentageCellColor(platform.data?.percentChangeOldest),
                            fontSize: tableTheme.fonts.small
                        }),
                        ...(metric.type === 'tnSales' && {
                            backgroundColor: platform.name === 'TN' ? 'rgba(33, 150, 243, 0.1)' : 'rgba(128, 128, 128, 0.05)',
                            fontWeight: platform.name === 'TN' ? 'bold' : 'normal',
                            color: platform.name === 'TN' ? tableTheme.colors.textPrimary : tableTheme.colors.textSecondary
                        })
                    }}
                >
                    {getMetricValue(metric.key)}
                </DataCell>
            ))}
        </TableRow>
    );
};



/**
 * EventHistoryTable - A reusable component for displaying historical event data across platforms
 * Now with transposed layout: platforms as rows, metrics as columns
 * @param props - Component props
 * @returns The rendered history table component
 */
const EventHistoryTable: React.FC<EventHistoryTableProps> = ({ selectedEvent }) => {
    // Prepare platform data by filtering out platforms with no data
    const availablePlatforms: PlatformData[] = PLATFORM_CONFIG
        .map(config => ({
            ...config,
            data: selectedEvent?.[config.name.toLowerCase() === 'tn' ? 'ticketNetwork' :
                config.name.toLowerCase() === 'vs' ? 'vivid' :
                    config.name.toLowerCase() === 'gc' ? 'goTickets' :
                        config.name.toLowerCase() as keyof EventItem] as TicketSource | null
        }))
        .filter(platform => platform.data);

    // Show empty state if no platform data is available
    if (availablePlatforms.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="textSecondary">
                    No historical data available
                </Typography>
            </Box>
        );
    }

    return (
        <TableContainer component={Paper} sx={{
            borderRadius: 2,
            border: `1px solid ${tableTheme.colors.borderColor}`,
            width: '100%',
        }}>
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ backgroundColor: tableTheme.colors.headerBackground }}>
                        <HeaderCell sx={{ minWidth: 50, maxWidth: 60, textAlign: 'center' }}>
                            Market
                        </HeaderCell>
                        {METRIC_CONFIG.map((metric) => (
                            <HeaderCell key={metric.key} align="center" sx={{
                                minWidth: 50,
                                maxWidth: 60,
                                ...(metric.type === 'tnSales' && {
                                    backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                    fontStyle: 'italic'
                                })
                            }}>
                                {metric.label}
                            </HeaderCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* Platform Rows - each platform gets its own row */}
                    {availablePlatforms.map((platform) => (
                        <PlatformRow
                            key={platform.name}
                            platform={platform}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default EventHistoryTable;
